import { format, isValid } from 'date-fns';
import { TFunction } from 'i18next';
import { lowerCase, upperFirst } from 'lodash';

import { getDateLocalizations } from '../../../core/utils/localization.ts';

export type FieldKind = 'NORMAL' | 'RISKY' | 'CHANNEL_UNIQUE' | 'SECRET';

export interface ConfigChange {
    path: string;
    section: string;
    kind: FieldKind;
    before: unknown;
    after: unknown;
    itemName?: string | null;
}

export interface ConfigPart {
    option: string;
    label: string;
    sections: string[];
}

export interface ConfigChannel {
    documentType: string;
    channelId: string;
    channelName: string;
    parts: ConfigPart[];
    onConfigChange: () => void;
}

export interface ConfigVersion {
    number: number;
    source: string;
    sourceName?: string | null;
    restoredFromNumber?: number | null;
    changedPaths: string[];
    createdAt: string;
    createdByName?: string | null;
    createdByEmail?: string | null;
}

export interface ConfigRiskyField {
    path: string;
    kind: FieldKind;
    value: unknown;
    version?: ConfigVersion | null;
}

const RULE_CONTAINER_TITLE_KEYS: Record<string, string> = {
    header_llm_extractor_config_container: 'configPage.fineTuning.headerTitle',
    line_items_llm_extractor_config_container: 'configPage.fineTuning.lineItemTitle',
};

const SECRET_KEY_SUFFIX = '__is_set';

const parseJson = (value: unknown) => (typeof value === 'string' ? JSON.parse(value) : value);

export const toConfigChanges = (changes: ConfigChange[] = []): ConfigChange[] =>
    changes.map((change) => ({ ...change, before: parseJson(change.before), after: parseJson(change.after) }));

export const isSecretPath = (path: string) => path.endsWith(SECRET_KEY_SUFFIX);

export const isApplicable = (change: ConfigChange) => change.kind !== 'CHANNEL_UNIQUE' && change.kind !== 'SECRET';

export const isPreselected = (change: ConfigChange) => isApplicable(change) && change.kind === 'NORMAL';

export const humanize = (key: string) => upperFirst(lowerCase(key.replace(SECRET_KEY_SUFFIX, '')));

export const getPathLabel = (change: ConfigChange, t: TFunction) => {
    const [section, ...keys] = change.path.split('.');
    if (section !== 'llm_rules') return keys.map(humanize).join(' › ');

    const [container, , key] = keys;
    return [
        RULE_CONTAINER_TITLE_KEYS[container] ? t(RULE_CONTAINER_TITLE_KEYS[container]) : humanize(container),
        change.itemName || t('configPage.history.change.rule'),
        key && humanize(key),
    ]
        .filter(Boolean)
        .join(' › ');
};

export const isWholeRule = (change: ConfigChange) =>
    change.section === 'llm_rules' && change.path.split('.').length === 3;

const fallbackPart = (section: string): ConfigPart => ({
    option: section,
    label: humanize(section),
    sections: [section],
});

const getChangedParts = (parts: ConfigPart[], sections: string[]) => [
    ...parts.filter((part) => part.sections.some((section) => sections.includes(section))),
    ...[...new Set(sections)]
        .filter((section) => !parts.some((part) => part.sections.includes(section)))
        .map(fallbackPart),
];

export const groupByPart = (parts: ConfigPart[], changes: ConfigChange[]) =>
    getChangedParts(
        parts,
        changes.map((change) => change.section)
    ).map((part) => ({ part, changes: changes.filter((change) => part.sections.includes(change.section)) }));

export const getChangedPartLabels = (parts: ConfigPart[], paths: string[]) =>
    getChangedParts(
        parts,
        paths.map((path) => path.split('.')[0])
    ).map((part) => part.label);

export const formatConfigDate = (date: string) =>
    isValid(new Date(date)) ? format(new Date(date), 'd MMM yyyy, HH:mm', { locale: getDateLocalizations() }) : '';
